import React, { useState, useEffect, useCallback, useContext } from "react";
import Select from "react-select";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import ModalTableInput from "../../../ui/ModalTableInput";
import { AuthContext } from "../../../../context/AuthContext";
import {
  swalConfirm,
  swalError,
  swalSuccess,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const ManageIndirectPOAttainments = () => {
  const { user } = useContext(AuthContext);
  const [indirectPOAttainments, setIndirectPOAttainments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [batchOptions, setBatchOptions] = useState([]);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [newAttainment, setNewAttainment] = useState({
    programId: "",
    batch: "",
    attainments: [],
  });

  const [editAttainment, setEditAttainment] = useState(null);
  const [initialEditAttainment, setInitialEditAttainment] = useState(null);
  const [isEditChanged, setIsEditChanged] = useState(false);

  const fetchPrograms = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/program/get-all/${user.instituteId}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (err) {
      console.error("Failed to fetch programs", err);
    }
  }, [user.instituteId]);

  const fetchStudentsAndBatches = useCallback(async () => {
    if (!user?.instituteId) return;
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/user/get-all/${user.instituteId}/student`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        // Filter students that have a batch
        const filteredUsers = data.filter(
          (stu) =>
            stu.role[0] === "student" &&
            stu.instituteId === user.instituteId &&
            stu.batch
        );

        // Extract unique batches and sort them
        const uniqueBatches = [
          ...new Set(filteredUsers.map((u) => u.batch)),
        ].sort((a, b) => b - a);
        setBatchOptions(
          uniqueBatches.map((b) => ({ value: b, label: String(b) }))
        );
      }
    } catch (err) {
      console.error("Failed to fetch students for batches", err);
    }
  }, [user.instituteId]);

  const fetchIndirectPOAttainments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/indirect-po-attainment/get-all/${user.instituteId}`,
        { credentials: "include" }
      );
      if (!response.ok)
        throw new Error("Failed to fetch Indirect PO Attainments");
      const data = await response.json();
      setIndirectPOAttainments(data);
    } catch (err) {
      swalError("Failed to fetch Indirect PO Attainments");
    } finally {
      setLoading(false);
    }
  }, [user.instituteId]);

  useEffect(() => {
    fetchPrograms();
    fetchStudentsAndBatches();
    fetchIndirectPOAttainments();
  }, [fetchPrograms, fetchStudentsAndBatches, fetchIndirectPOAttainments]);

  const programOptions = programs.map((p) => ({
    value: p._id,
    label: p.name,
    outcomes: p.programOutcomes || [],
    psOutcomes: p.PSOutcomes || [],
  }));

  const handleCreateClick = () => {
    setNewAttainment({
      programId: "",
      batch: "",
      attainments: [],
    });
    setIsCreateModalOpen(true);
  };

  const handleEditClick = (item) => {
    setEditAttainment({
      id: item._id,
      programId: item.programId?._id || item.programId,
      batch: item.batch,
      attainments: item.attainments || [],
    });
    setInitialEditAttainment({
      programId: item.programId?._id || item.programId,
      batch: item.batch,
      attainments: item.attainments || [],
    });
    setIsEditModalOpen(true);
    setIsEditChanged(false);
  };

  const handleDeleteClick = async (id) => {
    const result = await swalConfirm(
      "Are you sure you want to delete this record?",
      "",
      "Yes, delete it!",
      "No, Cancel"
    );

    if (result.isConfirmed) {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/indirect-po-attainment/delete/${id}`,
          { method: "DELETE", credentials: "include" }
        );
        if (response.ok) {
          swalSuccess("Deleted successfully!");
          fetchIndirectPOAttainments();
        } else {
          throw new Error("Failed to delete");
        }
      } catch (err) {
        swalError(err.message);
      }
    }
  };

  const handleAttainmentChange = (poCode, field, value, isEdit) => {
    const target = isEdit ? editAttainment : newAttainment;
    const setter = isEdit ? setEditAttainment : setNewAttainment;

    const currentAttainments = [...(target.attainments || [])];
    const existingIndex = currentAttainments.findIndex(
      (att) => att.poCode === poCode
    );

    if (existingIndex >= 0) {
      currentAttainments[existingIndex] = {
        ...currentAttainments[existingIndex],
        [field]: value,
      };
    } else {
      currentAttainments.push({
        poCode: poCode,
        attainment: field === "attainment" ? value : "",
        source: field === "source" ? value : "",
      });
    }

    setter({ ...target, attainments: currentAttainments });
  };

  const renderAttainmentsTable = (isEdit = false) => {
    const data = isEdit ? editAttainment : newAttainment;
    const selectedProgramId = data.programId;
    const selectedProgram = programs.find((p) => p._id === selectedProgramId);
    const programOutcomes = selectedProgram?.programOutcomes || [];
    const programSpecificOutcomes = selectedProgram?.PSOutcomes || [];

    if (!selectedProgramId) {
      return (
        <p style={{ color: "#666", fontStyle: "italic" }}>
          Please select a program first.
        </p>
      );
    }

    if (programOutcomes.length === 0 && programSpecificOutcomes.length === 0) {
      return (
        <p style={{ color: "#666", fontStyle: "italic" }}>
          No Program Outcomes or PSOs defined.
        </p>
      );
    }

    const allOutcomes = [...programOutcomes, ...programSpecificOutcomes];

    const rows = allOutcomes.map((outcome) => {
      const existingAttainment = (data.attainments || []).find(
        (att) => att.poCode === outcome.code
      );
      return {
        poCode: outcome.code,
        attainment: existingAttainment?.attainment || "",
        source: existingAttainment?.source || "",
      };
    });

    const headers = [
      { key: "poCode", label: "Code", width: "150px", required: true },
      {
        key: "attainment",
        label: "Attainment",
        width: "150px",
        required: true,
        placeholder: "e.g., 2.5",
      },
      {
        key: "source",
        label: "Source",
        width: "1fr",
        required: false,
        placeholder: "e.g., Exit Survey",
      },
    ];

    const renderCell = (header, row) => {
      switch (header.key) {
        case "poCode":
          return (
            <input
              type="text"
              value={row.poCode}
              disabled
              className="modal-table-input"
              style={{
                backgroundColor: "#f5f5f5",
                cursor: "not-allowed",
                color: "#666",
              }}
            />
          );
        case "attainment":
          return (
            <input
              type="number"
              value={row.attainment}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleAttainmentChange(row.poCode, "attainment", "", isEdit);
                } else {
                  const num = parseFloat(val);
                  if (!isNaN(num) && num >= 0 && num <= 3) {
                    handleAttainmentChange(
                      row.poCode,
                      "attainment",
                      val,
                      isEdit
                    );
                  }
                }
              }}
              required
              placeholder={header.placeholder}
              disabled={isSubmitting}
              min="0"
              max="3"
              step="0.25"
              className="modal-table-input"
            />
          );
        case "source":
          return (
            <input
              type="text"
              value={row.source}
              onChange={(e) =>
                handleAttainmentChange(
                  row.poCode,
                  "source",
                  e.target.value,
                  isEdit
                )
              }
              placeholder={header.placeholder}
              disabled={isSubmitting}
              className="modal-table-input"
            />
          );
        default:
          return null;
      }
    };

    return (
      <div className="form-group" style={{ marginTop: "20px" }}>
        <label>Indirect PO & PSO Attainments</label>
        <ModalTableInput
          headers={headers}
          rows={rows}
          onRowChange={() => {}}
          onRowRemove={() => {}}
          onRowAdd={null}
          renderCell={renderCell}
          addButtonText=""
        />
      </div>
    );
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/indirect-po-attainment/add`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...newAttainment,
            instituteId: user.instituteId,
          }),
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error("Failed to create");
      swalSuccess("Created successfully!");
      setIsCreateModalOpen(false);
      fetchIndirectPOAttainments();
    } catch (err) {
      swalError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/indirect-po-attainment/update/${editAttainment.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editAttainment),
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error("Failed to update");
      swalSuccess("Updated successfully!");
      setIsEditModalOpen(false);
      fetchIndirectPOAttainments();
    } catch (err) {
      swalError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (editAttainment && initialEditAttainment) {
      const isChanged =
        JSON.stringify(editAttainment) !==
        JSON.stringify(initialEditAttainment);
      setIsEditChanged(isChanged);
    }
  }, [editAttainment, initialEditAttainment]);

  const columns = [
    {
      key: "program",
      label: "Program",
      render: (item) => item.programId?.name || "N/A",
    },
    {
      key: "batch",
      label: "Batch",
      render: (item) => item.batch || "N/A",
    },
    {
      key: "edit",
      label: "",
      sortable: false,
      render: (item) => (
        <button className="edit-btn" onClick={() => handleEditClick(item)}>
          <i className="fa-solid fa-pen-to-square"></i>
        </button>
      ),
    },
    {
      key: "delete",
      label: "",
      sortable: false,
      render: (item) => (
        <button
          className="delete-btn"
          onClick={() => handleDeleteClick(item._id)}
        >
          <i className="fa-solid fa-trash-can"></i>
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="header">
        <button className="create-btn" onClick={handleCreateClick}>
          Add Indirect PO Attainment +
        </button>
      </div>
      <div className="manage-container">
        {loading ? (
          <Loader />
        ) : (
          <Table
            data={indirectPOAttainments}
            columns={columns}
            caption="Indirect PO Attainments"
          />
        )}
      </div>

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        maxWidth="50%"
      >
        <h2>Add Indirect PO Attainment</h2>
        <form onSubmit={handleCreateSubmit}>
          <div className="form-group">
            <label>
              Program <span style={{ color: "red" }}>*</span>
            </label>
            <Select
              options={programOptions}
              value={programOptions.find(
                (op) => op.value === newAttainment.programId
              )}
              onChange={(op) =>
                setNewAttainment({
                  ...newAttainment,
                  programId: op?.value || "",
                })
              }
              required
              placeholder="Select Program"
            />
          </div>
          <div className="form-group">
            <label>
              Batch <span style={{ color: "red" }}>*</span>
            </label>
            <Select
              options={batchOptions}
              value={batchOptions.find(
                (op) => op.value === newAttainment.batch
              )}
              onChange={(op) =>
                setNewAttainment({ ...newAttainment, batch: op?.value || "" })
              }
              required
              placeholder="Select Batch"
            />
          </div>
          {renderAttainmentsTable(false)}
          <button type="submit" className="btn-submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create"}
          </button>
        </form>
      </Modal>

      {editAttainment && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          maxWidth="50%"
        >
          <h2>Edit Indirect PO Attainment</h2>
          <form onSubmit={handleEditSubmit}>
            <div className="form-group">
              <label>
                Program <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                options={programOptions}
                value={programOptions.find(
                  (op) => op.value === editAttainment.programId
                )}
                onChange={(op) =>
                  setEditAttainment({
                    ...editAttainment,
                    programId: op?.value || "",
                  })
                }
                required
                placeholder="Select Program"
              />
            </div>
            <div className="form-group">
              <label>
                Batch <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                options={batchOptions}
                value={batchOptions.find(
                  (op) => String(op.value) === String(editAttainment.batch)
                )}
                onChange={(op) =>
                  setEditAttainment({
                    ...editAttainment,
                    batch: op?.value || "",
                  })
                }
                required
                placeholder="Select Batch"
              />
            </div>
            {renderAttainmentsTable(true)}
            <button
              type="submit"
              className="btn-submit"
              disabled={!isEditChanged || isSubmitting}
            >
              {isSubmitting ? "Updating..." : "Update"}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default ManageIndirectPOAttainments;
